import get from "lodash/get";
import intersectionBy from "lodash/intersectionBy";

// components
import Tag from "@/shared/Tag";

// helpers
import useCurrency from "@/hooks/useCurrency";
import { formatCurrency } from "@/helpers/currencyUtils";
import separatedNumber from "@/helpers/separatedNumber";
import { getSelectedValues } from "../WhereSelect/helpers";
import { FEATURED_LIST, AGE_RANGE_OPTIONS, TARGET_AUDIENCE_OPTIONS, WEEKLY_FOOTFALL_MAX_VALUE } from "@/components/Form/MoreFiltersForm/helpers";
import { SPACE_SIZES } from "../SpaceSizeDropdown/constants";
import collections from "@/api/query/collections";
import popularSearches from "@/api/query/popularSearches";
import countryStateCity from "@/api/query/countryStateCity";
import purposes from "@/api/query/purposes";
import dayjs from "@/helpers/dayjs";

// assets
import styles from "@/assets/styles/components/search-criteria-tags.module.scss";
import { SPACE_TYPE_OPTIONS } from "@/globals/global.constants";


interface ISearchCriteriaTags {
  queryParams: Record<string, string>;
  onSetQuery?: (obj: Record<string, unknown>) => void;
  isClosable?: boolean;
  isShowMainFilterTags?: boolean;
}

const SearchCriteriaTags = ({ queryParams, onSetQuery, isClosable, isShowMainFilterTags }: ISearchCriteriaTags) => {
  const { data: collectionsList = [] } = collections.useCollectionsList({ params: { is_publish: true } });
  const { data: keyCriteria = [] } = popularSearches.useGetSearches();
  const { data: countriesResp } = countryStateCity.useCountriesFilters(
    { only_published: true }, { enabled: isShowMainFilterTags }
  );
  const { data: purposesResp } = purposes.usePurposesList({ config: { enabled: isShowMainFilterTags } });

  const [currentCurrency] = useCurrency();

  const RANGE_VALUE_SYMBOL = {
    rate_daily__range: "",
    gen_z__range: "%",
    gen_y__range: "%",
    gen_x__range: "%",
    baby_boomer__range: "%",
    weekly_footfall__range: "",
    spending__range: "",
    dwell_time__range: "min",
    abc1__range: "%",
    female__range: "%",
    male__range: "%",
  }

  const RANGE_SEARCH_LIST = [
    { key: "rate_daily__range", title: "Budget" },
    ...TARGET_AUDIENCE_OPTIONS,
    ...AGE_RANGE_OPTIONS,
    { key: "female__range", title: "Female" },
    { key: "male__range", title: "Male" },
  ]

  // methods
  const handleRemoveBooleanTypeSearches = (value: string) => {

    onSetQuery && onSetQuery({ [value]: null });
  }

  const handleCheckListingTypeSearches = (key: string, id: number, selectedList: { id: number }[]) => {
    const newList = selectedList.filter(item => item.id !== id);

    onSetQuery && onSetQuery({ [key]: newList.map(item => item.id).join("__") });
  }
  
  // render
  const renderTile = (item: { name: string, onClose?: () => void }) => (
    <Tag
      className={styles["locl-search-criteria-tags__box"]}
      icon={null}
      color="dark-gold"
      closable={isClosable}
      onClose={item.onClose || (() => ({})) }
      key={item.name}
      data-testid="locl-search-criteria-tags-box"
    >
      { item.name}
    </Tag>
  )

  const renderCollectionTile = () => {
    const selectedCollections = get(queryParams, "collections__in", "")?.split("__").map(item => ({ id: +item }));

    const currentCollections = intersectionBy((collectionsList || []), selectedCollections, "id");

    return currentCollections.map((item) => renderTile({
      name: item.name,
      onClose: () => handleCheckListingTypeSearches("collections__in", item.id, selectedCollections)
    }))
  }

  const renderFeaturedTile = () => {
    const selectedFeatured = FEATURED_LIST.filter(item => get(
      queryParams,
      (item as { value: string, label: string }).value)
    )
      .map(item => (
        {
          name: (item as { value: string, label: string }).label,
          onClose: () => handleRemoveBooleanTypeSearches((item as { value: string, label: string }).value)
        }
      ));

    return selectedFeatured.map((item) => renderTile(item))
  }

  const renderSpaceRequirementsTile = () => {
    const selectedSpaceRequirements = get(queryParams, "popular_searches__in", "")?.split("__").map(item => ({ id: +item }));

    const currentSpaceRequirements = intersectionBy((keyCriteria || []), selectedSpaceRequirements, "id");

    return currentSpaceRequirements.map((item) => renderTile({
      name: item.name as string,
      onClose: () => handleCheckListingTypeSearches("popular_searches__in", item.id as number, selectedSpaceRequirements)
    }))
  }

  const renderRangedTile = () => {
    const handleCreateRangeTileName = (key: keyof typeof RANGE_VALUE_SYMBOL, title: string, value: string[]) => {
      if (key === "rate_daily__range" || key === "spending__range") {
        return `${title}: ${formatCurrency(value[0], currentCurrency)} - ${formatCurrency(value[1], currentCurrency)}`;
      }
      
      if (key === "dwell_time__range") {
        return `${title}: ${value[0]}-${value[1]} ${RANGE_VALUE_SYMBOL[key]}`
      }

      if (key === "weekly_footfall__range") {
        return `${title}: ${
          Number(value[1]) >= WEEKLY_FOOTFALL_MAX_VALUE
            ? separatedNumber(WEEKLY_FOOTFALL_MAX_VALUE)
            : separatedNumber(value[0] as unknown as number)
          }-${
          Number(value[1]) >= WEEKLY_FOOTFALL_MAX_VALUE
            ? `+${separatedNumber(WEEKLY_FOOTFALL_MAX_VALUE)}`
            : separatedNumber(value[1] as unknown as number)
        }`
      }

      return `${title}: ${value[0]}-${value[1]}${RANGE_VALUE_SYMBOL[key as keyof typeof RANGE_VALUE_SYMBOL]}`
    }

    const selectedRange = RANGE_SEARCH_LIST.filter(item => get(queryParams, item.key))
      .map(item => ({
        name: handleCreateRangeTileName(item.key as keyof typeof RANGE_VALUE_SYMBOL, item.title, get(queryParams, item.key, "").split("__")),
        onClose: () => handleRemoveBooleanTypeSearches(item.key)
      }));

    return selectedRange.map(renderTile)
  }

  const renderLocationTile = () => {
    if (get(queryParams, "city__in")) {
      const city__in = get(queryParams, "city__in", "").split("__").map(item => +item);
  
      const selectedCityOptions = getSelectedValues(countriesResp, city__in);
      
      let tileName = "Location: ";
      selectedCityOptions.forEach((item, index) => {
        if (index > 0) {
          tileName += `, ${item.label}`;
        } else {
          tileName += item.label;
        }
      })

      return renderTile({ name: tileName })
    }
  }

  const renderDateTile = () => {
    const startDate = get(queryParams, "start_date");
    const endDate = get(queryParams, "end_date");

    if (startDate && endDate) {
      
      return renderTile({ name: `Date: ${dayjs(startDate).format("MMM DD, YYYY")} - ${dayjs(endDate).format("MMM DD, YYYY")}` })
    }
  }

  const renderPurposeTile = () => {
    const selectedPurpose = get(queryParams, "purpose__in");

    if (selectedPurpose) {
      const purposeName = (purposesResp || []).find(item => +item.id === +selectedPurpose);

      return purposeName?.name && renderTile({ name: `Use of space: ${purposeName?.name}` });
    }
  }

  const renderSizeTile = () => {
    const size__in = get(queryParams, "size__in");
    const width = get(queryParams, "width__gte");
    const length = get(queryParams, "length__gte");

    if (size__in) {
      const sizeLabel = SPACE_SIZES.find(item => item.value === size__in)?.label;

      return renderTile({ name: `Space size: ${sizeLabel}` });
    }

    if (width || length) {
      const sizesArray = [width, length].filter(Boolean).map(item => `${item}m`);

      return renderTile({ name: `Space size: ${sizesArray.join(" x ")}` });
    }
  }

  const renderTypeTile = () => {
    const selectedType = get(queryParams, "type__in");

    if (!selectedType) {
      return null;
    }

    const selectedTypeAsArray = selectedType.split("__");

    const typeLabel = selectedTypeAsArray.length > 1
      ? "I'm flexible"
      : SPACE_TYPE_OPTIONS.find(item => item.value === +selectedTypeAsArray)?.label;

    if (!typeLabel) {
      return null
    }

    return renderTile({ name: `Space type: ${typeLabel}` });
  }

  return (
    <div className={styles["locl-search-criteria-tags"]}>
      {
        isShowMainFilterTags && (<>
          {renderLocationTile()}
          {renderDateTile()}
          {renderPurposeTile()}
          {renderSizeTile()}
          {renderTypeTile()}
        </>)
      }
      {renderCollectionTile()}
      {renderFeaturedTile()}
      {renderSpaceRequirementsTile()}
      {renderRangedTile()}
    </div>
  )
};

export default SearchCriteriaTags;
